import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { triggerToast } from '@/components'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { Option, Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/text-area'
import { UserAvatar } from '@/components/UserProfileCard'
import { useActionableCommentStore } from '@/features/actionable-comment'
import { useMarkThreadActionable } from '@/features/annotation-management'
import {
  GET_TEAMWORK_PAGINATION_LIMIT,
  UserMappingSource,
} from '@/features/user-mapping'
import { useUserMappingList } from '@/features/user-mapping/hooks'
import {
  ActionType,
  ThreadActionableType,
  ThreadActionType,
  ThreadPriority,
} from '@/schema'
import { DEFAULT_PAGE, MAX_TEXT_LENGTH, SortDirection } from '@/utils/constants'

interface ActionableFormData {
  actionType: string
  assignee?: string
  priority?: string
  programmerInstructions: string
}

const defaultFormValues: ActionableFormData = {
  actionType: '',
  assignee: undefined,
  priority: 'medium',
  programmerInstructions: '',
}

const ACTION_TYPE_OPTIONS = Object.keys(ActionType).map((key) => ({
  value: key,
  label: ActionType[key as keyof typeof ActionType],
}))

// Priority options - defined in desired order: Low -> Medium -> High
// Note: Select component sorts alphabetically, so these will display as: High, Low, Medium
// To maintain desired order, we use labels that sort correctly
// Priority labels are now translated in the component using useTranslation hook

export const ActionableDialog: React.FC = () => {
  const { t } = useTranslation()
  const {
    actionableDialogOpen,
    threadId,
    assigneeId,
    isChangeToActionable,
    setActionableDialogOpen,
    setIsCheckingSync,
  } = useActionableCommentStore()

  const {
    data: { data: userMappingsData = [] } = {},
    isLoading: isLoadingUserMappings,
  } = useUserMappingList({
    page: DEFAULT_PAGE,
    rowsPerPage: GET_TEAMWORK_PAGINATION_LIMIT,
    sort: { column: 'teamwork_user_id', direction: SortDirection.ASC },
    source: UserMappingSource.MAPPED_ONLY,
    enabled: actionableDialogOpen,
  })

  const matchedUser = useMemo(() => {
    if (!assigneeId) return null

    return userMappingsData.find((user) => user.vista_user_id === assigneeId)
  }, [assigneeId, userMappingsData])

  const { mutate: markThreadActionable, isPending } = useMarkThreadActionable()

  // Priority options with translated labels
  const priorityOptions = [
    { value: 'low', label: t('tflViewer.actionableDialog.priority.low') },
    { value: 'medium', label: t('tflViewer.actionableDialog.priority.medium') },
    { value: 'high', label: t('tflViewer.actionableDialog.priority.high') },
  ]

  // Initialize React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ActionableFormData>({
    defaultValues: defaultFormValues,
  })

  // Reset form when dialog closes or when matched user changes
  useEffect(() => {
    if (!actionableDialogOpen) {
      reset(defaultFormValues)
      return
    }

    reset({
      ...defaultFormValues,
      ...(matchedUser?.vista_user_id && {
        assignee: matchedUser.vista_user_id,
      }),
    })
  }, [actionableDialogOpen, matchedUser, reset])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setActionableDialogOpen({ open: newOpen })
    },
    [setActionableDialogOpen]
  )

  const onSubmit = (data: ActionableFormData) => {
    const selectedUser = userMappingsData.find(
      (user) => user.vista_user_id === data.assignee
    )

    markThreadActionable(
      {
        id: threadId || '',
        data: {
          action_type: data.actionType as ThreadActionType,
          actionable_type: ThreadActionableType.ACTIONABLE,
          assignee_id: selectedUser?.vista_user_id || undefined,
          priority: data.priority
            ? (data.priority?.toLowerCase() as ThreadPriority)
            : undefined,
          programmer_instructions: data.programmerInstructions,
        },
      },
      {
        onSuccess: () => {
          triggerToast(
            'success',
            t('tflViewer.actionableDialog.messages.success')
          )

          setIsCheckingSync(true)
          setTimeout(() => {
            setActionableDialogOpen({ open: false })
          })
        },
        onError: () => {
          triggerToast('error', t('tflViewer.actionableDialog.messages.error'))
        },
      }
    )
  }

  return (
    <Dialog open={actionableDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="!overflow-hidden flex flex-col max-h-[95dvh]">
          <DialogHeader className="space-y-0.5 flex-shrink-0">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {t(
                isChangeToActionable
                  ? 'tflViewer.actionableDialog.titleChangeToActionable'
                  : 'tflViewer.actionableDialog.title'
              )}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t(
                isChangeToActionable
                  ? 'tflViewer.actionableDialog.descriptionChangeToActionable'
                  : 'tflViewer.actionableDialog.description'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 px-6 pb-1 -mx-6 overflow-x-hidden overflow-y-auto">
            <form
              id="actionable-form"
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
            >
              {/* Action Type Field */}
              <div className="space-y-1">
                <label htmlFor="actionType" className="text-sm font-medium">
                  {t('tflViewer.actionableDialog.fields.actionType')}
                </label>
                <Controller
                  name="actionType"
                  control={control}
                  rules={{
                    required: t(
                      'tflViewer.actionableDialog.validation.actionTypeRequired'
                    ),
                  }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      fullWidth
                      placeholder={t(
                        'tflViewer.actionableDialog.placeholders.selectActionType'
                      )}
                      options={ACTION_TYPE_OPTIONS}
                    />
                  )}
                />
                {errors.actionType && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.actionType.message}
                  </p>
                )}
              </div>

              {/* Assignee Field */}
              <div className="space-y-1">
                <label htmlFor="assignee" className="text-sm font-medium">
                  {t('tflViewer.actionableDialog.fields.assignee')}
                </label>
                <Controller
                  name="assignee"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || ''}
                      onChange={field.onChange}
                      fullWidth
                      placeholder={t(
                        'tflViewer.actionableDialog.placeholders.selectAssignee'
                      )}
                      clearable
                      options={
                        userMappingsData.map((user) => ({
                          value: user.vista_user_id || '',
                          label: (
                            <div className="flex flex-col">
                              <div className="text-sm font-medium leading-5">
                                {user.teamwork_username}
                              </div>
                              <div className="text-xs leading-4 text-gray-500">
                                {user.teamwork_user_email || '-'}
                              </div>
                            </div>
                          ),
                          iconLeft: (
                            <UserAvatar
                              avatarUrl={user.vista_avatar_url || ''}
                              userName={user.teamwork_username || ''}
                              className="size-7"
                              textClassName="text-xs"
                            />
                          ),
                        })) as Option[]
                      }
                      disabled={
                        isLoadingUserMappings || !userMappingsData.length
                      }
                    />
                  )}
                />
              </div>

              {/* Priority Field */}
              <div className="space-y-1">
                <label htmlFor="priority" className="text-sm font-medium">
                  {t('tflViewer.actionableDialog.fields.priority')}
                </label>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || ''}
                      onChange={field.onChange}
                      fullWidth
                      placeholder={t(
                        'tflViewer.actionableDialog.placeholders.selectPriority'
                      )}
                      clearable
                      options={priorityOptions}
                      disableSorting
                    />
                  )}
                />
              </div>

              {/* Programmer Instructions Field */}
              <div className="space-y-1">
                <label
                  htmlFor="programmerInstructions"
                  className="text-sm font-medium"
                >
                  {t(
                    'tflViewer.actionableDialog.fields.programmerInstructions'
                  )}
                </label>
                <Controller
                  name="programmerInstructions"
                  control={control}
                  rules={{
                    required: t(
                      'tflViewer.actionableDialog.validation.programmerInstructionsRequired'
                    ),
                    maxLength: {
                      value: MAX_TEXT_LENGTH,
                      message: t(
                        'tflViewer.actionableDialog.validation.programmerInstructionsMaxLength',
                        {
                          max: MAX_TEXT_LENGTH,
                        }
                      ),
                    },
                  }}
                  render={({ field }) => (
                    <Textarea
                      id="programmerInstructions"
                      {...field}
                      className="bg-white border-solid rounded-md px-lg py-md gap-md border-primary text-md min-h-[100px] resize-none"
                      placeholder={t(
                        'tflViewer.actionableDialog.placeholders.enterProgrammerInstructions'
                      )}
                      maxLength={MAX_TEXT_LENGTH}
                    />
                  )}
                />
                {errors.programmerInstructions && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.programmerInstructions.message}
                  </p>
                )}
              </div>
            </form>
          </div>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3 flex-shrink-0">
            <Button
              type="button"
              variant="default"
              onClick={() => handleOpenChange(false)}
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white text-[--Colors-Gray-light-700]"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="actionable-form"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent text-white bg-brand-solid hover:bg-brand-solid-hover"
              variant="default"
              disabled={!isValid || isPending}
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
